import { AxiosError } from "axios";
import { useCallback } from "react";
import { useForm } from "react-hook-form";

import { toastSuccessMessage } from "../../../utils/alerts";
import {
  BUTTON_EVENT_CATEGORY,
  GENERAL_EVENT_CATEGORY,
  trackEvent,
} from "../../../utils/amplitude";

import {
  getGeneralFormDefaultValues,
  getGradeFormDefaultValues,
  getSectionInfoFormDefaultValues,
  getVirtualClassFormDefaultValues,
} from "./form-defaults";
import {
  buildGeneralPayload,
  buildGradePayload,
  buildSectionInfoPayload,
  buildVirtualClassPayload,
} from "./payload-builders";
import {
  type GeneralFormValues,
  type GradeFormValues,
  type SectionInfoFormValues,
  TAB_INDEX,
  type VirtualClassFormValues,
} from "./types";

import type { SectionType } from "components/server-types";
import { t } from "i18n/i18n";
import { storeApi } from "store/index";
import { useIsArchived } from "utils/hooks/use-is-archived";
import { isLinkedSection } from "utils/publishing";
import { isAVEnabled } from "utils/waffle-flags";

type UseSectionSettingsArgs = {
  section: SectionType | null | undefined;
  canAuthorCourse: boolean;
  onClose: () => void;
  setTabIndex: (index: number) => void;
};

type TabSaveEntry = {
  tabIndex: number;
  isDirty: boolean;
  trigger: () => Promise<boolean>;
  buildPayload: () => Partial<SectionType>;
};

export const useSectionSettings = ({
  section,
  canAuthorCourse,
  onClose,
  setTabIndex,
}: UseSectionSettingsArgs) => {
  const generalForm = useForm<GeneralFormValues>({
    defaultValues: getGeneralFormDefaultValues(section),
    shouldUnregister: false,
  });
  const sectionInfoForm = useForm<SectionInfoFormValues>({
    defaultValues: getSectionInfoFormDefaultValues(section),
    shouldUnregister: false,
  });
  const gradeForm = useForm<GradeFormValues>({
    defaultValues: getGradeFormDefaultValues(section),
    shouldUnregister: false,
    mode: "onChange",
  });
  const virtualClassForm = useForm<VirtualClassFormValues>({
    defaultValues: getVirtualClassFormDefaultValues(section),
    shouldUnregister: false,
  });

  const { isArchived: isArchivedSection } = useIsArchived();

  const isLinked = section ? isLinkedSection(section) : false;
  const isArchived = section ? isArchivedSection(section) : false;

  const { isDirty: isGeneralDirty, isValid: isGeneralValid } = generalForm.formState;
  const { isDirty: isSectionInfoDirty, isValid: isSectionInfoValid } = sectionInfoForm.formState;
  const { isDirty: isGradeDirty, isValid: isGradeValid } = gradeForm.formState;
  const { isDirty: isVirtualClassDirty, isValid: isVirtualClassValid } = virtualClassForm.formState;

  const isAnyDirty =
    isGeneralDirty || isSectionInfoDirty || isGradeDirty || (isAVEnabled() && isVirtualClassDirty);

  const isAllValid =
    isGeneralValid && isSectionInfoValid && isGradeValid && (!isAVEnabled() || isVirtualClassValid);

  const getTabSaveEntries = useCallback((): TabSaveEntry[] => {
    const entries: TabSaveEntry[] = [
      {
        tabIndex: TAB_INDEX.general,
        isDirty: generalForm.formState.isDirty,
        trigger: () => generalForm.trigger(),
        buildPayload: () => buildGeneralPayload(generalForm.getValues()),
      },
      {
        tabIndex: TAB_INDEX.sectionInfo,
        isDirty: sectionInfoForm.formState.isDirty,
        trigger: () => sectionInfoForm.trigger(),
        buildPayload: () => buildSectionInfoPayload(sectionInfoForm.getValues(), canAuthorCourse),
      },
      {
        tabIndex: TAB_INDEX.grade,
        isDirty: gradeForm.formState.isDirty,
        trigger: () => gradeForm.trigger(),
        buildPayload: () => buildGradePayload(gradeForm.getValues()),
      },
    ];

    if (isAVEnabled()) {
      entries.push({
        tabIndex: TAB_INDEX.virtualClass,
        isDirty: virtualClassForm.formState.isDirty,
        trigger: () => virtualClassForm.trigger(),
        buildPayload: () => buildVirtualClassPayload(virtualClassForm.getValues()),
      });
    }

    return entries;
  }, [canAuthorCourse, generalForm, gradeForm, sectionInfoForm, virtualClassForm]);

  const saveAll = useCallback(async () => {
    if (!section || isLinked) {
      return;
    }

    const tabSaveEntries = getTabSaveEntries();

    const validationResults = await Promise.all(
      tabSaveEntries.map(async (entry) => ({
        tabIndex: entry.tabIndex,
        isValid: await entry.trigger(),
      })),
    );

    const firstInvalid = validationResults.find((result) => !result.isValid);
    if (firstInvalid) {
      setTabIndex(firstInvalid.tabIndex);
      return;
    }

    const payload: Partial<SectionType> & { id: string } = { id: section.id };
    let generalWasDirty = false;

    for (const entry of tabSaveEntries) {
      if (entry.isDirty) {
        Object.assign(payload, entry.buildPayload());
        if (entry.tabIndex === TAB_INDEX.general) {
          generalWasDirty = true;
        }
      }
    }

    if (Object.keys(payload).length === 1) {
      return;
    }

    trackEvent("Section Settings - Save changes", BUTTON_EVENT_CATEGORY);

    const originalProjectorZoom = section.projector_zoom;

    try {
      await storeApi.sections.partial_update(payload, { skipToast: true });
      toastSuccessMessage(t("success.toasts.updated_section_settings"));

      if (generalWasDirty && payload.projector_zoom !== undefined) {
        const zoomHasChanged = payload.projector_zoom !== originalProjectorZoom;
        if (zoomHasChanged) {
          trackEvent("Section Settings - Projector view zoom level", {
            ...GENERAL_EVENT_CATEGORY,
            zoom: payload.projector_zoom,
          });
        }
      }

      onClose();
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        const validationError = error.response?.data as Record<string, string[]>;
        if (validationError?.section_score_percentages) {
          gradeForm.setError("root.section_score_percentages", {
            type: "400",
            message: t("settings.section_score_percentages_must_sum_to_100"),
          });
          setTabIndex(TAB_INDEX.grade);
        }
      }
    }
  }, [getTabSaveEntries, gradeForm, isLinked, onClose, section, setTabIndex]);

  return {
    generalForm,
    sectionInfoForm,
    gradeForm,
    virtualClassForm,
    isAnyDirty,
    isAllValid,
    isLinked,
    isArchived,
    saveAll,
  };
};
